import {makeAutoObservable, reaction, runInAction} from 'mobx'
import {inject, injectable} from 'inversify'
import {CallBackProps} from 'react-joyride'

import {TUserGuideStore} from '@/components/containers/UserGuideContainer/TUserGuideStore'
import {getSteps} from '@/constants/user-guide/get-steps'
import {UserGuideStep} from '@/stores/user-guide/models/UserGuideStep'
import {AuthStoreInterface, AuthStoreSymbol, VirtualTerminalStoreSymbol} from '@/stores'
import {MainLayoutStoreSymbol} from '@/layouts'
import {IMainLayoutStore} from '@/layouts/MainLayout/IMainLayoutStore'
import {ROUTES} from '@/router/routes'
import {getRouterStore} from '@/router/utils'
import {updateUserGuideReadStatus} from '@/stores/user-guide/services/fetchers'
import {UpdateUserGuiderRequest} from '@/stores/user-guide/models/UpdateUserGuiderRequest'
import {ProfileStoreSymbol} from '@/pages/Profile/Profile'
import {ProfileStoreInterface} from '@/pages/Profile'
import {LoadingState, MenuDataItem} from '@/types'
import {TVirtualTerminalStore} from '@/pages/VirtualTerminal'

@injectable()
export class UserGuideStore implements TUserGuideStore {
    private _authStore: AuthStoreInterface
    private _profileStore: ProfileStoreInterface
    private _virtualTerminalStore: TVirtualTerminalStore
    private _mainLayoutStore: IMainLayoutStore

    constructor(
        @inject(AuthStoreSymbol) authStore: AuthStoreInterface,
        @inject(ProfileStoreSymbol) profileStore: ProfileStoreInterface,
        @inject(VirtualTerminalStoreSymbol) virtualTerminalStore: TVirtualTerminalStore,
        @inject(MainLayoutStoreSymbol) mainLayoutStore: IMainLayoutStore
    ) {
        this._authStore = authStore
        this._profileStore = profileStore
        this._virtualTerminalStore = virtualTerminalStore
        this._mainLayoutStore = mainLayoutStore

        makeAutoObservable(this)

        reaction(
            () => this._virtualTerminalStore.isVirtualTerminalsLoaded,
            () =>
                (this.steps = getSteps(
                    this._virtualTerminalStore.isVirtualTerminalAvailable,
                    this.onSkipPress
                ))
        )

        reaction(
            () => ({
                permissions: this._authStore.permissions
            }),
            () => {
                this.steps = getSteps(
                    this._virtualTerminalStore.isVirtualTerminalAvailable,
                    this.onSkipPress
                )
            }
        )

        reaction(
            () => ({
                guidePassed: this._profileStore?.portalGuideViewed,
                loadingState: this._profileStore?.loadingState,
                activated: this._authStore?.activated
            }),
            ({guidePassed, loadingState, activated}) => {
                if (!!!guidePassed && loadingState === LoadingState.DONE && activated) {
                    runInAction(() => {
                        this.isModalVisible = true
                    })
                } else {
                    runInAction(() => {
                        this.isModalVisible = false
                    })
                }
            }
        )
    }

    run = false
    tourActive = false
    isModalVisible = false
    stepIndex = 0

    setRun = (run: boolean) => {
        this.run = run
    }

    setStepIndex = (stepIndex: number) => {
        this.stepIndex = stepIndex
    }

    handleCallback = async (data: CallBackProps) => {
        const {action, index, lifecycle, type} = data

        const nextStep = this.steps[index + 1]

        const goNextStep = () => {
            if (nextStep) {
                setTimeout(() => {
                    this.setStepIndex(nextStep.step)
                }, 300)
                if (nextStep.link) {
                    getRouterStore().push(nextStep.link)
                }
                // Open submenu if the next step target is inside a submenu
                this.openSubmenuForStep(nextStep)
            }
        }

        if (type === 'step:after' && index !== this.steps?.length - 1) {
            if (action === 'next') {
                //setTimeout fixes the issue where the step card was displayed in the wrong position
                setTimeout(() => goNextStep(), 0)
            }
        } else if (index === this.steps?.length - 1 && lifecycle === 'complete') {
            await this.onSkipPress()
            getRouterStore().push(ROUTES.home)
        }
    }

    openSubmenuForStep = (step: UserGuideStep) => {
        if (!step?.target || step.target === 'body') return

        const selector = (step.target as string).replace(/^\./, '')
        const menuItems = this._mainLayoutStore.menuItems
        const currentOpenKeys = new Set(this._mainLayoutStore.openKeys || [])

        const findParentPath = (
            items: MenuDataItem[],
            targetSelector: string,
            parentPath?: string
        ): string | undefined => {
            for (const item of items) {
                if (item.className === targetSelector) {
                    return parentPath
                }

                if (item.children) {
                    const found = findParentPath(item.children, targetSelector, item.path)
                    if (found) {
                        return found
                    }
                }
            }
            return undefined
        }

        const parentPath = findParentPath(menuItems, selector)
        if (parentPath && !currentOpenKeys.has(parentPath)) {
            currentOpenKeys.add(parentPath)
            this._mainLayoutStore.setOpenKeys(Array.from(currentOpenKeys))
        }
    }

    setTourActive = (tourActive: boolean) => {
        this.tourActive = tourActive
    }

    setIsModalVisible = (isModalVisible: boolean) => {
        this.isModalVisible = isModalVisible
    }

    onGotItClick = () => {
        this.setIsModalVisible(false)
        this.setRun(true)

        const openKeys = new Set<string>()
        const menuItems = this._mainLayoutStore.menuItems

        const findMenuItem = (
            items: MenuDataItem[],
            selector: string
        ): MenuDataItem | undefined => {
            for (const item of items) {
                if (item.className === selector) {
                    if (item.path && item.children?.length) {
                        openKeys.add(item.path)
                    }
                    return item
                }

                if (item.children) {
                    const child = findMenuItem(item.children, selector)
                    if (child) {
                        if (item.path && item.children?.length) {
                            openKeys.add(item.path)
                        }
                        return child
                    }
                }
            }
            return undefined
        }

        for (const step of this.steps) {
            const selector = (step.target as string).replace(/^\./, '')
            findMenuItem(menuItems, selector)
        }

        this._mainLayoutStore.setOpenKeys(Array.from(openKeys))
    }

    onSkipPress = async () => {
        this._mainLayoutStore.setOpenKeys([])
        const request: UpdateUserGuiderRequest = {
            portalGuideViewed: true,
            merchantId: this._profileStore?.profileData?.merchantId
        }

        await updateUserGuideReadStatus(request)

        this.setIsModalVisible(false)
        this.setRun(false)
        this.setTourActive(false)
        this.setStepIndex(0)
    }

    steps: UserGuideStep[] = []
}
